import { createContext, forwardRef, useContext, useLayoutEffect, useRef, useState, type InputHTMLAttributes, type KeyboardEvent, type ReactNode } from "react";
import { Icon, type IconName } from "./Icon";
import { cx } from "./kit";

/**
 * Command palette con la stessa struttura di shadcn/cmdk: Command, CommandInput, CommandList,
 * CommandEmpty, CommandGroup, CommandItem, CommandShortcut, CommandSeparator.
 * Il filtro lo fa chi la usa (rende solo le voci che corrispondono); qui c'è la navigazione da tastiera.
 */
const Ctx = createContext<{ active: string | null; setActive: (v: string) => void; listRef: React.RefObject<HTMLDivElement | null> } | null>(null);
const useCommand = () => useContext(Ctx)!;

const items = (list: HTMLElement | null) => [...(list?.querySelectorAll<HTMLElement>("[data-command-item]") ?? [])];

/** `resetKey`: quando cambia (testo cercato, pagina) la selezione torna alla prima voce, come in cmdk. */
export function Command({ children, className, onKeyDown, resetKey }: { children: ReactNode; className?: string; onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void; resetKey?: string }) {
  const [active, setActive] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // se la voce attiva sparisce (nuovo filtro o pagina), si riparte dalla prima
  useLayoutEffect(() => {
    const all = items(listRef.current);
    if (!all.some((el) => el.dataset.value === active)) setActive(all[0]?.dataset.value ?? "");
  });
  useLayoutEffect(() => {
    setActive(items(listRef.current)[0]?.dataset.value ?? "");
    listRef.current?.scrollTo({ top: 0 });
  }, [resetKey]);
  const move = (delta: number) => {
    const all = items(listRef.current);
    if (!all.length) return;
    const i = Math.max(0, all.findIndex((el) => el.dataset.value === active));
    const next = all[(i + delta + all.length) % all.length]!;
    setActive(next.dataset.value!);
    next.scrollIntoView({ block: "nearest" });
  };
  return (
    <Ctx.Provider value={{ active, setActive, listRef }}>
      <div
        className={cx("flex flex-col min-h-0", className)}
        onKeyDown={(e) => {
          onKeyDown?.(e);
          if (e.defaultPrevented) return;
          if (e.key === "ArrowDown") (e.preventDefault(), move(1));
          if (e.key === "ArrowUp") (e.preventDefault(), move(-1));
          if (e.key === "Enter") {
            e.preventDefault();
            items(listRef.current).find((el) => el.dataset.value === active)?.click();
          }
        }}
      >
        {children}
      </div>
    </Ctx.Provider>
  );
}

export const CommandInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { chip?: ReactNode; trailing?: ReactNode }>(function CommandInput({ chip, trailing, className, ...rest }, ref) {
  const { active } = useCommand();
  return (
    <div className="flex items-center gap-2.5 px-4 h-14 border-b border-line shrink-0">
      <Icon name="search" size={18} color="var(--color-subtle)" />
      {chip}
      <input ref={ref} role="combobox" aria-expanded="true" aria-controls="command-list" aria-activedescendant={active ? `cmd-${active}` : undefined} className={cx("flex-1 h-full border-0 outline-none text-base bg-transparent min-w-0", className)} {...rest} />
      {trailing}
    </div>
  );
});

export function CommandList({ children }: { children: ReactNode }) {
  const { listRef } = useCommand();
  return (
    <div ref={listRef} id="command-list" role="listbox" className="max-h-[440px] overflow-y-auto p-1.5 scroll-py-8">
      {children}
    </div>
  );
}

export function CommandEmpty({ children }: { children: ReactNode }) {
  return <div className="px-3 py-8 text-center text-sm text-muted">{children}</div>;
}

export function CommandGroup({ heading, children }: { heading: ReactNode; children: ReactNode }) {
  return (
    <div role="group" className="py-1">
      <div className="px-3 pt-1.5 pb-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-subtle">{heading}</div>
      {children}
    </div>
  );
}

export function CommandSeparator() {
  return <div role="separator" className="h-px bg-line-soft -mx-1.5 my-1" />;
}

/** Voce della palette. `value` deve essere unico; `color` tinge l'icona (colore del modulo). */
export function CommandItem({ value, onSelect, icon, color, children, sub, meta, shortcut, chevron }: { value: string; onSelect: () => void; icon: IconName; color?: string; children: ReactNode; sub?: ReactNode; meta?: ReactNode; shortcut?: string; chevron?: boolean }) {
  const { active, setActive } = useCommand();
  const on = active === value;
  return (
    <button
      type="button"
      id={`cmd-${value}`}
      role="option"
      aria-selected={on}
      data-command-item
      data-value={value}
      onMouseMove={() => !on && setActive(value)}
      onClick={onSelect}
      className={cx("w-full flex items-center gap-3 px-3 min-h-11 py-1.5 rounded-lg text-left text-sm cursor-pointer", on && "bg-hover")}
    >
      <span className="h-7 w-7 rounded-md flex items-center justify-center shrink-0" style={{ background: color ? `color-mix(in srgb, ${color} 13%, var(--color-surface))` : "var(--color-line-soft)", color: color ?? "var(--color-text2)" }}>
        <Icon name={icon} size={15} />
      </span>
      <span className="flex-1 min-w-0 flex flex-col leading-tight">
        <span className="truncate font-medium">{children}</span>
        {sub && <span className="text-xs text-muted truncate">{sub}</span>}
      </span>
      {meta && <span className="text-xs text-muted shrink-0">{meta}</span>}
      {shortcut && <CommandShortcut keys={shortcut} />}
      {chevron && <Icon name="chevron-right" size={15} className="text-subtle shrink-0" />}
    </button>
  );
}

/** Scorciatoia mostrata a destra della voce: "G P" diventa due tasti. */
export function CommandShortcut({ keys }: { keys: string }) {
  return (
    <span className="flex items-center gap-1 shrink-0" aria-label={`Scorciatoia ${keys}`}>
      {keys.split(" ").map((k, i) => (
        <kbd key={i} className="min-w-5 h-5 px-1 rounded border border-line bg-ground text-[11px] font-sans text-muted flex items-center justify-center">
          {k}
        </kbd>
      ))}
    </span>
  );
}
